/* eslint-disable */
import type { NSFWDetector } from '../nsfw/NSFWDetector'

/**
 * NSFW detection via `canvas.drawImage()` – works in **every** browser.
 *
 * Use cases:
 *  1. Browsers without Insertable Streams (Safari, Firefox) where
 *     {@link NSFWTransformer} cannot be added to the media pipeline.
 *  2. Monitoring subscriber `<video>` elements (any browser).
 *  3. Primary publisher monitoring when the pipeline slot is occupied
 *     by background / color filters.
 *
 * Captures a frame from the target `<video>` element at a configurable
 * interval and sends it to {@link NSFWDetector} for classification.
 */
export class CanvasNSFWFallback {
  private detector: NSFWDetector
  private canvas: HTMLCanvasElement
  private ctx: CanvasRenderingContext2D
  private intervalId: number | null = null
  private sampleIntervalMs: number
  private videoElement: HTMLVideoElement | null = null
  private analyzing = false

  /**
   * @param detector         - The shared {@link NSFWDetector} instance.
   * @param sampleIntervalMs - Milliseconds between frame captures
   *                           (default 1500 ms).
   */
  constructor (detector: NSFWDetector, sampleIntervalMs = 1500) {
    this.detector = detector
    this.sampleIntervalMs = sampleIntervalMs
    this.canvas = document.createElement('canvas')
    this.ctx = this.canvas.getContext('2d')!
  }

  /**
   * Start periodically sampling the given video element.
   *
   * @param videoElement - The `<video>` to monitor for NSFW content.
   */
  start (videoElement: HTMLVideoElement): void {
    this.stop()
    this.videoElement = videoElement

    this.intervalId = window.setInterval(() => {
      this.captureAndClassify()
    }, this.sampleIntervalMs)
  }

  /** Stop sampling and clear the interval timer. */
  stop (): void {
    if (this.intervalId !== null) {
      window.clearInterval(this.intervalId)
      this.intervalId = null
    }
    this.videoElement = null
  }

  /** Whether the fallback is currently running. */
  isActive (): boolean {
    return this.intervalId !== null
  }

  /**
   * Change the sampling interval. If currently running, the timer is
   * automatically restarted with the new interval.
   *
   * @param ms - New interval in milliseconds.
   */
  setSampleInterval (ms: number): void {
    this.sampleIntervalMs = ms

    if (this.isActive() && this.videoElement) {
      const el = this.videoElement
      this.stop()
      this.start(el)
    }
  }

  /** Stop sampling and release references. */
  destroy (): void {
    this.stop()
  }

  private async captureAndClassify (): Promise<void> {
    if (this.analyzing) return
    if (!this.detector.isModelLoaded() || !this.videoElement) return
    if (this.videoElement.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) return

    this.analyzing = true
    try {
      const width = this.videoElement.videoWidth || 224
      const height = this.videoElement.videoHeight || 224

      if (this.canvas.width !== width || this.canvas.height !== height) {
        this.canvas.width = width
        this.canvas.height = height
      }

      this.ctx.drawImage(this.videoElement, 0, 0, width, height)
      await this.detector.classify(this.canvas)
    } catch (_) {
      // Must not break the video playback
    } finally {
      this.analyzing = false
    }
  }
}
